import { Link } from "react-router-dom";
import { PhoneLink } from "../components/PhoneCTA";
import Disclaimer from "../components/Disclaimer";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";

const faq = [
  {
    q: "Do I need a live consultation before any therapy?",
    a: "Yes. Every prescription treatment requires a live video (or in-clinic) consultation with a licensed VidaVital provider who independently reviews your history and goals.",
  },
  {
    q: "Do you accept insurance?",
    a: "No. VidaVital Medical is a self-pay practice. Medicare, Medicaid, and commercial insurance are not billed for these services. HSA/FSA may be usable for eligible services — confirm with your plan administrator.",
  },
  {
    q: "How much does a consultation cost?",
    a: "Consultation and treatment pricing is discussed during scheduling. Estimated monthly starting prices for each therapy are displayed on the Peptide Library. Final pricing depends on your individualized plan, labs, and provider recommendations.",
  },
  {
    q: "Can you use HSA/FSA?",
    a: "Some services may be HSA/FSA eligible. Ask your plan administrator whether your specific care qualifies.",
  },
  {
    q: "How does telemedicine work?",
    a: "You schedule a time slot, receive a secure video link, meet the licensed provider on the platform, and — if a therapy is medically appropriate — the provider handles prescribing and any needed follow-ups.",
  },
  {
    q: "What states do you serve?",
    a: "In-clinic visits are available in Nevada, New Mexico, Florida, Wisconsin, and Nebraska. Telehealth availability depends on the provider's state licensure — this is confirmed at scheduling.",
  },
  {
    q: "How are medications shipped?",
    a: "When a therapy is prescribed after evaluation, discreet shipping is arranged through our partner pharmacies where appropriate. Some medications may require in-clinic pickup.",
  },
  {
    q: "Do I need bloodwork before my first appointment?",
    a: "Not always. The provider decides whether recent labs are sufficient or whether new testing is needed. Some therapies more strongly indicate baseline testing — the Peptide Library shows the bloodwork status for each.",
  },
  {
    q: "How often are follow-ups?",
    a: "Follow-up cadence is individualized. Most patients follow up within the first 30–60 days and then quarterly, but the provider tailors this to your therapy and history.",
  },
  {
    q: "Can I speak with someone before scheduling?",
    a: "Yes — call our care coordinators at (888) 773-8677, submit a request through /schedule, or ask our AI care assistant on any page.",
  },
];

export default function ProviderFAQ() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  return (
    <div data-testid="provider-faq-page">
      <SEO
        title="Provider FAQ — Consultations, Telehealth, Insurance & Labs"
        description="Answers to common questions about VidaVital Medical peptide consultations, telehealth, self-pay pricing, HSA/FSA, state licensure, labs, shipping, and follow-up cadence."
        path="/provider-faq"
        schema={faqSchema}
        breadcrumbs={[{ name: "Provider FAQ", path: "/provider-faq" }]}
      />
      <section className="vv-hero">
        <div className="vv-container py-16">
          <Breadcrumbs items={[{ name: "Provider FAQ", path: "/provider-faq" }]} />
          <div className="vv-hero-eyebrow">Provider FAQ</div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl mt-3">Common questions, answered.</h1>
          <p className="mt-4 max-w-2xl">Everything patients typically ask about VidaVital Medical, telemedicine, pricing, insurance, labs, and how care works.</p>
        </div>
      </section>

      <section className="vv-container py-14 grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2 space-y-4">
          {faq.map((f, i) => (
            <details key={f.q} data-testid={`faq-${i}`} className="vv-card p-5 group open:shadow-lg">
              <summary className="cursor-pointer font-display text-lg flex justify-between items-center list-none">
                {f.q}
                <span className="text-[color:var(--vv-gold-deep)] group-open:rotate-45 transition-transform text-xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-sm text-stone-700 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
        <aside className="space-y-4">
          <div className="vv-card p-6">
            <div className="text-xs uppercase tracking-widest text-stone-500">Still have questions?</div>
            <div className="mt-3"><PhoneLink location="faq_page" /></div>
            <Link to="/request-consultation" className="vv-btn-primary text-sm mt-4 inline-flex">Schedule Consultation</Link>
          </div>
          <Disclaimer />
        </aside>
      </section>
    </div>
  );
}
